import React, { PureComponent, Fragment } from 'react'
import cn from 'classnames'
import { isEmpty } from 'util/objects'
import { isWindows, getVisibilityState } from 'util/browser'
import { debounce } from 'util/functions'
import PropTypes from 'prop-types'
import ResizeObserver from 'resize-observer-polyfill'
import MatrixClientPeg from 'matrix-react-sdk/lib/MatrixClientPeg'
import RoomViewStore from 'matrix-react-sdk/lib/stores/RoomViewStore'
import RoomView from 'matrix-react-sdk/lib/components/structures/RoomView'
import { MatrixClient } from 'matrix-js-sdk'
import matrixDispatcher from 'matrix-react-sdk/lib/dispatcher'
import ResizeNotifier from 'matrix-react-sdk/lib/utils/ResizeNotifier'
import AvailabilityBar from 'subapps/chat/components/AvailabilityBar'
import { buildRoomAliasFromId } from 'ducks/chat/utils/rooms'

import 'gfm.css/gfm.css'
import 'highlight.js/styles/github.css'
import 'draft-js/dist/Draft.css'

import RoomHeader from '../RoomHeader'
import ExpirationStatusBar from '../ExpirationStatusBar'
import { styles } from './styles'
import '../themes/groove/css/groove.less'

const MARKASREAD_DEBOUNCE_TIMEOUT = 5000

export default class Chat extends PureComponent {
  static childContextTypes = {
    matrixClient: PropTypes.instanceOf(MatrixClient),
    authCache: PropTypes.object, //  eslint-disable-line react/forbid-prop-types
  }

  constructor(props) {
    super(props)

    const { roomId } = props
    this.state = {
      roomId,
      resizeNotifier: new ResizeNotifier(),
    }
    this.observer = new ResizeObserver(this.onResize)
    this.wrapperRef = React.createRef()
  }

  getChildContext() {
    return {
      matrixClient: MatrixClientPeg.get(),
      authCache: {
        auth: {},
        lastUpdate: 0,
      },
    }
  }

  componentDidMount() {
    this.initComponent()
  }

  componentDidUpdate(prevProps) {
    const { roomId, room, accountId, eventId } = this.props
    this.initComponent()
    if (!this.isInitialized) return
    const {
      roomId: previousRoomId,
      room: previousRoom,
      accountId: previousAccountId,
    } = prevProps
    // Typical usage (don't forget to compare props):

    if (roomId !== previousRoomId || accountId !== previousAccountId) {
      this.loadRoom(roomId, eventId, accountId)
      this.delayedMarkRoomAsReadIfRequired.cancel()
      this.markRoomAsReadIfRequired(previousRoomId, previousRoom, true)
      this.markRoomAsReadIfRequired(roomId, room, false)
      this.stopObservingResize()
    } else {
      this.startObservingResize()
      if (!room.isRead && previousRoom.isRead !== room.isRead) {
        this.delayedMarkRoomAsReadIfRequired(roomId, room, true)
      }
    }
  }

  componentWillUnmount() {
    const { roomId, room } = this.props
    if (!this.isInitialized) return
    this.removeStoreListener()
    this.stopObservingResize()
    this.delayedMarkRoomAsReadIfRequired.cancel()
    this.markRoomAsReadIfRequired(roomId, room, true)
    this.isInitialized = false
  }

  onRoomViewStoreUpdate = () => {
    const { roomId, isLoaded, isLoading, fetchRoomById } = this.props
    const { matrixRoomId } = this.state
    const newMatrixRoomId = RoomViewStore.getRoomId()
    if (matrixRoomId !== newMatrixRoomId) {
      this.hasTriedLoading = false
      this.setState({
        newMatrixRoomId,
        roomLoading: RoomViewStore.isRoomLoading(),
      })
      if (!isLoaded && !isLoading && !this.hasTriedLoading) {
        this.hasTriedLoading = true
        fetchRoomById({ id: roomId })
      }
    }
  }

  onResize = () => {
    const { resizeNotifier } = this.state
    resizeNotifier.notifyWindowResized()
  }

  initComponent() {
    const { roomId, room, eventId, accountId, isMatrixReady } = this.props
    if (isMatrixReady && !this.isInitialized) {
      this.isInitialized = true
      this.loadRoom(roomId, eventId, accountId)
      this.markRoomAsReadIfRequired(roomId, room, false)
      this.startObservingResize()
    }
  }

  startObservingResize() {
    const { resizeNotifier } = this.state
    if (!this.observing && this.wrapperRef.current) {
      this.observing = true
      resizeNotifier.on('middlePanelResized', this.dispatchTimelineResize)
      this.observer.observe(this.wrapperRef.current)
    }
  }

  stopObservingResize() {
    const { resizeNotifier } = this.state

    if (this.observing) {
      this.observing = false
      resizeNotifier.removeListener(
        'middlePanelResized',
        this.dispatchTimelineResize
      )
      if (this.wrapperRef.current) {
        this.observer.unobserve(this.wrapperRef.current)
      }
    }
  }

  dispatchTimelineResize() {
    // For PersistentElement
    matrixDispatcher.dispatch({ action: 'timeline_resize' })
  }

  markRoomAsReadIfRequired = (roomId, room, isClose) => {
    const { markAsRead } = this.props
    // When tab is in the background, dont automatically mark conversations as read
    const isTabInFocus = getVisibilityState() === 'visible'
    if (
      !isEmpty(room) &&
      !room.isRead &&
      isTabInFocus &&
      (!room.ignoreNextClose || !isClose)
    ) {
      markAsRead(roomId, { afterUpdateMessage: false })
    }
  }

  delayedMarkRoomAsReadIfRequired = debounce(
    this.markRoomAsReadIfRequired,
    MARKASREAD_DEBOUNCE_TIMEOUT
  )

  loadRoom(roomId, eventId, accountId) {
    if (roomId && accountId) {
      this.removeStoreListener()
      this.roomStoreToken = RoomViewStore.addListener(
        this.onRoomViewStoreUpdate
      )
      matrixDispatcher.dispatch({
        action: 'view_room',
        room_alias: buildRoomAliasFromId(roomId, accountId),
        event_id: eventId,
      })
    }
  }

  removeStoreListener() {
    if (this.roomStoreToken) {
      this.roomStoreToken.remove()
    }
  }

  render() {
    const {
      hasError,
      errorMessage,
      roomId,
      isLoaded,
      eventId,
      isMatrixReady,
    } = this.props
    const { newMatrixRoomId } = this.state
    if (isLoaded && this.isInitialized && isMatrixReady) {
      return (
        <Fragment>
          {newMatrixRoomId && (
            <div
              css={styles.matrixChatWrapper}
              className={cn('mx_MatrixChat_wrapper', {
                mx_OsWindows: isWindows(),
              })}
              ref={this.wrapperRef}
            >
              <RoomHeader conversationId={roomId}>
                <div className="conversation-banners">
                  <AvailabilityBar />
                  <ExpirationStatusBar />
                </div>
              </RoomHeader>
              <div className="mx_MatrixChat">
                <RoomView
                  collapsedRhs={false}
                  disabled={false}
                  oobData={{}}
                  thirdPartyInvite={{}}
                  viaServers={[]}
                  key={newMatrixRoomId}
                  showUploadBar={false}
                  initialEventId={eventId}
                />
              </div>
            </div>
          )}
        </Fragment>
      )
    }
    if (hasError) {
      return <div>{errorMessage}</div>
    }
    return null
  }
}
